import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import DevLayout from '../../components/developer/DevLayout'
import { useAuth } from '../../context/AuthContext'
import api, { getImageUrl } from '../../utils/api'

interface Property {
    _id: string; title: string; price: number; city: string; area: string;
    status: string; type: string; images: string[]; isActive: boolean;
    isFeatured: boolean; createdAt: string;
}

interface Project {
    _id: string;
    name: string;
    propertyType: string;
    location: string;
    status: 'Pending' | 'Approved' | 'Rejected';
    createdAt: string;
    featuredImage?: string;
    slug?: string;
}

export default function DevMyProperty() {
    const { token } = useAuth()
    const [properties, setProperties] = useState<Property[]>([])
    const [projects, setProjects] = useState<Project[]>([])
    const [loading, setLoading] = useState(true)
    const [activeTab, setActiveTab] = useState<'listings' | 'projects'>('projects')
    const [deleting, setDeleting] = useState<string | null>(null)
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null)
    const [itemToDelete, setItemToDelete] = useState<{ id: string, name: string, type: 'project' | 'property' } | null>(null)

    useEffect(() => {
        if (!token) { setLoading(false); return }
        setLoading(true);
        
        const fetchAll = async () => {
            try {
                const [propData, projData] = await Promise.all([
                    api.getMyProperties(token),
                    api.getMyProjects(token)
                ]);
                if (Array.isArray(propData)) setProperties(propData);
                if (Array.isArray(projData)) setProjects(projData);
            } catch (err) {
                console.error("Data synchronization failed", err);
            } finally {
                setLoading(false);
            }
        };

        fetchAll();
    }, [token])

    const handleDelete = async (id: string, title: string) => {
        setDeleting(id)
        const res = await api.deleteProperty(token!, id)
        if (res.message === 'Property deleted successfully') {
            setProperties(p => p.filter(x => x._id !== id))
            setMsg({ type: 'success', text: 'Listing deleted successfully.' })
        } else {
            setMsg({ type: 'error', text: res.message || 'Delete failed.' })
        }
        setDeleting(null)
        setItemToDelete(null)
    }

    const handleDeleteProject = async (id: string, name: string) => {
        setDeleting(id)
        try {
            const res = await api.deleteProject(token!, id)
            if (res.message.toLowerCase().includes('success')) {
                setProjects(p => p.filter(x => x._id !== id))
                setMsg({ type: 'success', text: 'Project deleted successfully.' })
            } else {
                setMsg({ type: 'error', text: res.message || 'Delete failed.' })
            }
        } catch (err) {
            setMsg({ type: 'error', text: 'System error during deletion.' })
        }
        setDeleting(null)
        setItemToDelete(null)
    }

    return (
        <DevLayout>
            <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between mb-5 gap-3">
                <div>
                    <h3 className="fw-black text-[#0F2A5F] mb-1 uppercase italic tracking-tight">Venture Pipeline</h3>
                    <p className="text-[#0F2A5F]/60 mb-0 small uppercase tracking-widest font-bold">Manage your active asset clusters and individual listings.</p>
                </div>
                <div className="d-flex align-items-center gap-3">
                    <div className="p-1 bg-[#0F2A5F]/5 rounded-pill d-inline-flex border border-[#0F2A5F]/10">
                        <button 
                            onClick={() => setActiveTab('projects')}
                            className={`btn btn-sm rounded-pill px-4 py-2 border-0 fw-black uppercase tracking-widest text-[10px] transition-all ${activeTab === 'projects' ? 'bg-[#0F2A5F] text-[#D4A017] shadow-sm' : 'text-muted hover:text-[#0F2A5F]'}`}
                        >
                            Major Projects
                        </button>
                        <button 
                            onClick={() => setActiveTab('listings')}
                            className={`btn btn-sm rounded-pill px-4 py-2 border-0 fw-black uppercase tracking-widest text-[10px] transition-all ${activeTab === 'listings' ? 'bg-[#0F2A5F] text-[#D4A017] shadow-sm' : 'text-muted hover:text-[#0F2A5F]'}`}
                        >
                            Properties
                        </button>
                    </div>
                    {activeTab === 'listings' ? (
                        <Link to="/developer-add-property" className="btn d-inline-flex align-items-center gap-2 shadow-lg px-4 py-3 rounded-4 border-0" style={{ backgroundColor: '#D4A017', color: '#fff' }}>
                            <i className="fa-solid fa-plus text-[10px]"></i> <span className="fw-black uppercase tracking-widest text-[10px]">Add Listing</span>
                        </Link>
                    ) : (
                        <Link to="/developer-add-project" className="btn d-inline-flex align-items-center gap-2 shadow-lg px-4 py-3 rounded-4 border-0" style={{ backgroundColor: '#0F2A5F', color: '#D4A017' }}>
                            <i className="fa-solid fa-city text-[10px]"></i> <span className="fw-black uppercase tracking-widest text-[10px]">Add Project</span>
                        </Link>
                    )}
                </div>
            </div>

            {msg && (
                <div className={`alert alert-${msg.type === 'success' ? 'success' : 'danger'} border-0 shadow-sm mb-4`}>
                    {msg.text}
                </div>
            )}

            <div className="card border-0 shadow-xl rounded-[2rem] overflow-hidden">
                <div className="px-5 py-4 bg-white border-bottom d-flex justify-content-between align-items-center">
                    <h5 className="mb-0 fw-black text-[#0F2A5F] uppercase italic">
                        {activeTab === 'projects' ? `Asset Clusters (${projects.length})` : `Individual Listings (${properties.length})`}
                    </h5>
                    <div className="h-1 bg-[#D4A017] rounded-pill w-16"></div>
                </div>

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border" style={{ color: '#0F2A5F' }} role="status"></div>
                        <p className="mt-3 text-[#0F2A5F] fw-black uppercase tracking-widest text-[10px]">Synchronizing Venture Data...</p>
                    </div>
                ) : activeTab === 'projects' ? (
                    // PROJECTS VIEW
                    projects.length === 0 ? (
                        <div className="text-center py-5 bg-white">
                            <i className="fa-solid fa-city fa-4x mb-4 opacity-50" style={{ color: '#0F2A5F' }}></i>
                            <h4 className="fw-black text-[#0F2A5F] uppercase italic">No Active Projects</h4>
                            <p className="text-[#0F2A5F]/60 mb-4 font-bold">Initialize your first large-scale development protocol.</p>
                            <Link to="/developer-add-project" className="btn px-5 py-3 shadow-lg rounded-4 border-0" style={{ backgroundColor: '#0F2A5F', color: '#D4A017' }}>
                                <i className="fa-solid fa-plus me-2"></i> <span className="fw-black uppercase tracking-widest text-[11px]">Deploy Project Node</span>
                            </Link>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle m-0">
                                <thead className="bg-[#0F2A5F]/5 py-4">
                                    <tr>
                                        <th className="ps-5 border-0 text-[10px] font-black text-[#0F2A5F]/50 uppercase tracking-widest">Project Cluster</th>
                                        <th className="border-0 text-[10px] font-black text-[#0F2A5F]/50 uppercase tracking-widest">Location Matrix</th>
                                        <th className="border-0 text-[10px] font-black text-[#0F2A5F]/50 uppercase tracking-widest">Approval Status</th>
                                        <th className="border-0 text-[10px] font-black text-[#0F2A5F]/50 uppercase tracking-widest">Deployment Date</th>
                                        <th className="pe-5 border-0 text-end text-[10px] font-black text-[#0F2A5F]/50 uppercase tracking-widest">Operations</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {projects.map(p => (
                                        <tr key={p._id} className="border-bottom border-gray-50">
                                            <td className="ps-5 py-4">
                                                <div className="d-flex align-items-center gap-3">
                                                    <div className="flex-shrink-0">
                                                        <div className="bg-[#0F2A5F]/5 rounded-4 d-flex align-items-center justify-content-center border" style={{ width: 70, height: 50 }}>
                                                            <i className="fa-solid fa-building text-[#0F2A5F] opacity-30"></i>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="fw-black text-[#0F2A5F] uppercase italic">{p.name}</div>
                                                        <small className="text-[#D4A017] fw-bold uppercase tracking-widest text-[9px]">{p.propertyType}</small>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="text-[#0F2A5F]/70 fw-bold small uppercase tracking-tight">
                                                    <i className="fa-solid fa-location-crosshairs me-2 opacity-50"></i>{p.location}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`badge px-3 py-2 rounded-pill fw-black uppercase tracking-widest text-[9px]`} style={{
                                                    background: p.status === 'Approved' ? '#ecfdf5' : p.status === 'Rejected' ? '#fef2f2' : '#fefce8',
                                                    color: p.status === 'Approved' ? '#10b981' : p.status === 'Rejected' ? '#ef4444' : '#d4a017',
                                                    border: `1px solid ${p.status === 'Approved' ? '#10b98120' : p.status === 'Rejected' ? '#ef444420' : '#d4a01720'}`
                                                }}>
                                                    {p.status}
                                                </span>
                                            </td>
                                            <td><small className="text-[#0F2A5F]/40 fw-black text-[10px]">{new Date(p.createdAt).toLocaleDateString('en-IN')}</small></td>
                                            <td className="pe-5 text-end">
                                                <div className="d-flex justify-content-end gap-2">
                                                     <Link to={`/developer-edit-project/${p._id}`} className="w-10 h-10 rounded-3 bg-gray-50 text-[#D4A017] flex items-center justify-center hover:bg-[#D4A017] hover:text-white transition-all shadow-sm border border-gray-100" title="Edit Project">
                                                          <i className="fa-solid fa-pencil text-xs"></i>
                                                     </Link>
                                                     <Link to={`/project-details/${p.slug || p._id}`} className="w-10 h-10 rounded-3 bg-gray-50 text-[#0F2A5F] flex items-center justify-center hover:bg-[#0F2A5F] hover:text-white transition-all shadow-sm border border-gray-100" title="View Portfolio">
                                                          <i className="fa-solid fa-eye text-xs"></i>
                                                     </Link>
                                                    <Link to={`/developer-inventory`} className="w-10 h-10 rounded-3 bg-gray-50 text-[#D4A017] flex items-center justify-center hover:bg-[#D4A017] hover:text-white transition-all shadow-sm border border-gray-100" title="Manage Inventory">
                                                        <i className="fa-solid fa-boxes-stacked text-xs"></i>
                                                    </Link>
                                                    <button className="w-10 h-10 rounded-3 bg-gray-50 text-danger flex items-center justify-center hover:bg-danger hover:text-white transition-all shadow-sm border border-gray-100"
                                                        disabled={deleting === p._id}
                                                        onClick={() => setItemToDelete({ id: p._id, name: p.name, type: 'project' })}
                                                        title="Delete Protocol"
                                                    >
                                                        {deleting === p._id
                                                            ? <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }}></span>
                                                            : <i className="fa-solid fa-trash-can text-xs"></i>
                                                        }
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )
                ) : (
                    // PROPERTIES VIEW
                    properties.length === 0 ? (
                        <div className="text-center py-5 bg-white">
                            <i className="fa-solid fa-house-circle-exclamation fa-4x mb-4 opacity-50" style={{ color: '#D4A017' }}></i>
                            <h4 className="fw-black text-[#0F2A5F] uppercase italic">No Individual Listings</h4>
                            <p className="text-[#0F2A5F]/60 mb-4 font-bold">Ready to showcase a specific asset? List your property now!</p>
                            <Link to="/developer-add-property" className="btn px-5 py-3 shadow-lg rounded-4 border-0" style={{ backgroundColor: '#D4A017', color: '#fff' }}>
                                <i className="fa-solid fa-plus me-2"></i> <span className="fw-black uppercase tracking-widest text-[11px]">Create New Listing</span>
                            </Link>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle m-0">
                                <thead className="bg-[#0F2A5F]/5 py-4">
                                    <tr>
                                        <th className="ps-5 border-0 text-[10px] font-black text-[#0F2A5F]/50 uppercase tracking-widest">Asset Details</th>
                                        <th className="border-0 text-[10px] font-black text-[#0F2A5F]/50 uppercase tracking-widest">Valuation</th>
                                        <th className="border-0 text-[10px] font-black text-[#0F2A5F]/50 uppercase tracking-widest">Status Matrix</th>
                                        <th className="border-0 text-[10px] font-black text-[#0F2A5F]/50 uppercase tracking-widest">Geographical Node</th>
                                        <th className="pe-5 border-0 text-end text-[10px] font-black text-[#0F2A5F]/50 uppercase tracking-widest">Operations</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {properties.map(p => (
                                        <tr key={p._id} className="border-bottom border-gray-50">
                                            <td className="ps-5 py-4">
                                                <div className="d-flex align-items-center gap-3">
                                                    <div className="flex-shrink-0">
                                                        {p.images?.length > 0 ? (
                                                            <img src={getImageUrl(p.images[0])} className="rounded-4 shadow-sm" style={{ width: 70, height: 50, objectFit: 'cover' }} alt={p.title} />
                                                        ) : (
                                                            <div className="bg-[#0F2A5F]/5 rounded-4 d-flex align-items-center justify-content-center border" style={{ width: 70, height: 50 }}>
                                                                <i className="fa-solid fa-image text-[#0F2A5F] opacity-30"></i>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <div className="fw-black text-[#0F2A5F] uppercase italic">{p.title}</div>
                                                        <small className="text-[#D4A017] fw-bold uppercase tracking-widest text-[9px]">{p.type}</small>
                                                    </div>
                                                </div>
                                            </td>
                                            <td><div className="fw-black text-[#0F2A5F] italic">₹{p.price?.toLocaleString()}</div></td>
                                            <td>
                                                <span className={`badge px-3 py-2 rounded-pill fw-black uppercase tracking-widest text-[9px]`} style={{
                                                    background: p.status === 'For Rent' ? '#eff6ff' : '#ecfdf5',
                                                    color: p.status === 'For Rent' ? '#3b82f6' : '#10b981',
                                                    border: `1px solid ${p.status === 'For Rent' ? '#3b82f615' : '#10b98115'}`
                                                }}>
                                                    {p.status}
                                                </span>
                                            </td>
                                            <td><span className="text-[#0F2A5F]/70 fw-bold small uppercase tracking-tight"><i className="fa-solid fa-location-dot me-2 opacity-50"></i>{p.city}</span></td>
                                            <td className="pe-5 text-end">
                                                <div className="d-flex justify-content-end gap-2">
                                                     <Link to={`/developer-edit-property/${p._id}`} className="w-10 h-10 rounded-3 bg-gray-50 text-[#D4A017] flex items-center justify-center hover:bg-[#D4A017] hover:text-white transition-all shadow-sm border border-gray-100" title="Edit Listing">
                                                          <i className="fa-solid fa-pencil text-xs"></i>
                                                     </Link>
                                                     <Link to={`/property-details/${p._id}`} className="w-10 h-10 rounded-3 bg-gray-50 text-[#0F2A5F] flex items-center justify-center hover:bg-[#0F2A5F] hover:text-white transition-all shadow-sm border border-gray-100" title="View Listing">
                                                          <i className="fa-solid fa-eye text-xs"></i>
                                                     </Link>
                                                    <button className="w-10 h-10 rounded-3 bg-gray-50 text-danger flex items-center justify-center hover:bg-danger hover:text-white transition-all shadow-sm border border-gray-100"
                                                        disabled={deleting === p._id}
                                                        onClick={() => setItemToDelete({ id: p._id, name: p.title, type: 'property' })}
                                                        title="Delete Protocol"
                                                    >
                                                        {deleting === p._id
                                                            ? <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }}></span>
                                                            : <i className="fa-solid fa-trash-can text-xs"></i>
                                                        }
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )
                )}
            </div>

            {itemToDelete && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-[#0F2A5F]/40 backdrop-blur-sm">
                    <div className="bg-white rounded-[2.5rem] w-full max-w-md overflow-hidden border border-[#0F2A5F]/10 shadow-2xl animate__animated animate__zoomIn animate__faster">
                        <div className="p-8 text-center">
                            <div className="w-20 h-20 bg-danger/10 text-danger rounded-full flex items-center justify-center mx-auto mb-6">
                                <i className="fa-solid fa-trash-can text-3xl"></i>
                            </div>
                            <h3 className="text-xl font-black text-[#0F2A5F] uppercase italic mb-3">Delete Protocol</h3>
                            <p className="text-[#0F2A5F]/60 font-medium mb-8">
                                Are you sure you want to delete <span className="font-bold text-[#0F2A5F]">"{itemToDelete.name}"</span>? 
                                {itemToDelete.type === 'project' && " This will also remove all associated inventory and assets."}
                                This action is permanent.
                            </p>
                            
                            <div className="grid grid-cols-2 gap-3">
                                <button 
                                    onClick={() => setItemToDelete(null)}
                                    className="py-4 bg-gray-50 text-[#0F2A5F] rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-gray-100 transition-all"
                                >
                                    Cancel
                                </button>
                                <button 
                                    onClick={() => itemToDelete.type === 'project' ? handleDeleteProject(itemToDelete.id, itemToDelete.name) : handleDelete(itemToDelete.id, itemToDelete.name)}
                                    disabled={deleting !== null}
                                    className="py-4 bg-danger text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-danger/20 hover:scale-105 active:scale-95 transition-all flex items-center justify-center gap-2"
                                >
                                    {deleting ? (
                                        <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }}></span>
                                    ) : "Delete Forever"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </DevLayout>
    )
}
